import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, MapPin, Store as StoreIcon } from "lucide-react";
import api from "@/api";
import { resolveAssetUrl, withTrailingSlash } from "@/config";
import { cachedFetch, getCacheEntry } from "@/utils/fetchCache";
import { StoreHoursBlock, type StoreHoursDisplay } from "./StoreHoursBlock";

const STORES_CACHE_KEY = "stores-public";
const STORES_TTL_MS = 10 * 60 * 1000;

interface Store {
  _id: string;
  storeName: string;
  address: string;
  landmark?: string;
  image: string;
  cityId?: {
    cityName: string;
  };
  stateId?: {
    stateName: string;
  };
  countryId?: {
    countryName: string;
  };
  hoursDisplay?: StoreHoursDisplay;
}

function StoreCard({ s }: { s: Store }) {
  return (
    <article className="h-full rounded-2xl overflow-hidden border border-border bg-surface hover:border-primary/40 transition-all group/card">
      <Link
        to={withTrailingSlash(`/store-locator?storeId=${encodeURIComponent(s._id)}`)}
        className="block h-full"
      >
        <div className="aspect-[4/3] overflow-hidden bg-surface-elevated">
          <img
            src={
              resolveAssetUrl(s.image) ||
              "https://placehold.co/800x600/1a1a1a/ffffff?text=Fonexpress"
            }
            alt={s.storeName}
            width={800}
            height={600}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover/card:scale-105"
          />
        </div>
        <div className="p-4">
          <h3
            className="font-display font-bold text-lg leading-tight truncate"
            title={s.storeName}
          >
            {s.storeName}
          </h3>
          <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
            <MapPin className="h-3 w-3 text-primary shrink-0" />
            <span
              className="truncate"
              title={`${s.address}${s.landmark ? `, ${s.landmark}` : ""} · ${[s.cityId?.cityName, s.stateId?.stateName, s.countryId?.countryName].filter(Boolean).join(", ")}`}
            >
              {s.address} {s.landmark ? `(${s.landmark})` : ""}{" "}
              {s.cityId?.cityName ? `· ${s.cityId.cityName}` : ""}
            </span>
          </p>
          <StoreHoursBlock
            hoursDisplay={s.hoursDisplay}
            compact
            className="mt-2"
          />
          <p className="mt-2 text-xs font-semibold text-primary">
            Open details and map
          </p>
        </div>
      </Link>
    </article>
  );
}

export function StoresCarousel() {
  const cachedStores = getCacheEntry(STORES_CACHE_KEY, STORES_TTL_MS) as Store[] | null;
  const [stores, setStores] = useState<Store[]>(cachedStores ?? []);
  const [loading, setLoading] = useState(!cachedStores);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchStores = async () => {
      try {
        const data = await cachedFetch(
          STORES_CACHE_KEY,
          async () => {
            const response = await api.get("/api/v1/website/stores/public");
            if (response.data?.isOk) {
              return response.data.data as Store[];
            }
            return [];
          },
          { ttlMs: STORES_TTL_MS }
        );
        if (!cancelled) {
          setStores(data || []);
        }
      } catch (error) {
        console.error("Error fetching stores:", error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchStores();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="container mx-auto px-4 lg:px-8 pt-6 pb-10 animate-pulse">
        <div className="h-8 w-64 bg-surface rounded-lg mb-4"></div>
        <div className="h-4 w-96 bg-surface rounded-lg mb-8"></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="aspect-[4/3] bg-surface rounded-2xl"></div>
          ))}
        </div>
      </div>
    );
  }

  if (stores.length === 0) return null;

  const marquee = stores.length > 1;
  const loopStores = marquee ? [...stores, ...stores] : stores;
  const durationSec = Math.max(120, stores.length * 8);

  return (
    <section className="container mx-auto px-4 lg:px-8 pt-6 pb-10">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p className="inline-flex items-center gap-2 text-sm uppercase tracking-widest text-primary font-semibold mb-2">
            <StoreIcon className="h-4 w-4" /> Visit Us
          </p>
          <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">
            Our{" "}
            <span className="text-gradient-brand">{stores.length}+ Stores</span>{" "}
            Nationwide
          </h2>
          <p className="mt-2 text-muted-foreground max-w-md">
            Walk into any Fonexpress branch for hands-on demos, expert advice
            and instant pickup.
          </p>
        </div>
        <Link
          to={withTrailingSlash("/store-locator")}
          className="inline-flex items-center gap-1.5 self-start sm:self-auto shrink-0 rounded-full border border-primary/30 bg-primary/5 px-4 py-2.5 text-sm font-semibold text-primary hover:bg-primary hover:text-white transition-colors"
        >
          See all stores
          <ChevronRight className="h-4 w-4" />
        </Link>
      </div>

      <div
        className="overflow-hidden"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div
          className={`flex w-max ${marquee ? "animate-marquee" : ""} motion-reduce:animate-none`}
          style={
            marquee
              ? {
                  animationDuration: `${durationSec}s`,
                  animationPlayState: paused ? "paused" : "running",
                }
              : undefined
          }
        >
          {loopStores.map((s, index) => (
            <div
              key={`${s._id}-${index}`}
              className="w-[272px] sm:w-[300px] lg:w-[320px] shrink-0 pr-4 lg:pr-6"
            >
              <StoreCard s={s} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
